import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkers, useWorkerPool, createWorker, updateWorker, deleteWorker, validateWorker, benchmarkPool, getCapacity, } from '../hooks/useWorkers';
// Campos (no secretos) y secretos por transporte. Reflejan lo que la API
// admite (apps/web-api/src/workers/routes.ts).
const TRANSPORT_FIELDS = {
    local: { options: [], secrets: [] },
    ssh: {
        options: [
            { key: 'host', label: 'host', placeholder: 'usuario@1.2.3.4' },
            { key: 'remote_bin', label: 'remote_bin', placeholder: 'gestiora' },
            { key: 'workdir', label: 'workdir', placeholder: '/tmp/gestiora-worker' },
        ],
        secrets: [
            { key: 'ssh_key', label: 'ssh_key (clave privada)' },
            { key: 'password', label: 'password' },
        ],
    },
    kaggle: {
        options: [
            { key: 'kernel', label: 'kernel', placeholder: 'tu-usuario/gestiora-worker' },
            { key: 'account_email', label: 'correo de la cuenta', placeholder: 'cuenta@ejemplo.com' },
        ],
        secrets: [
            { key: 'kaggle_token', label: 'kaggle_token (kaggle.json)' },
            { key: 'kaggle_password', label: 'contraseña de la cuenta (opcional)' },
        ],
    },
    http: {
        options: [
            { key: 'url', label: 'url', placeholder: 'https://xxxx.loca.lt' },
            { key: 'account_email', label: 'correo de la cuenta', placeholder: 'cuenta@ejemplo.com' },
        ],
        secrets: [{ key: 'token', label: 'token (cabecera)' }],
    },
    petals: {
        options: [
            { key: 'endpoint', label: 'endpoint API', placeholder: 'https://chat.petals.dev/api/v1' },
            { key: 'model', label: 'modelo LLM', placeholder: 'meta-llama/Llama-2-70b-chat-hf' },
        ],
        secrets: [{ key: 'api_key', label: 'api_key (opcional)' }],
    },
    runpod: {
        options: [
            { key: 'endpoint_id', label: 'Serverless Endpoint ID (opcional para modo API)', placeholder: 'xxxxxx' },
            { key: 'host', label: 'host SSH (opcional para modo VM)', placeholder: 'root@xxxx.runpod.net' },
            { key: 'remote_bin', label: 'remote_bin (modo VM)', placeholder: 'gestiora' },
            { key: 'workdir', label: 'workdir (modo VM)', placeholder: '/tmp/gestiora-worker' },
        ],
        secrets: [
            { key: 'api_key', label: 'RunPod API Key (para Serverless)' },
            { key: 'ssh_key', label: 'ssh_key (clave privada para VM)' },
        ],
    },
    lambdalabs: {
        options: [
            { key: 'host', label: 'host IP', placeholder: 'ubuntu@1.2.3.4' },
            { key: 'remote_bin', label: 'remote_bin', placeholder: 'gestiora' },
            { key: 'workdir', label: 'workdir', placeholder: '/tmp/gestiora-worker' },
        ],
        secrets: [
            { key: 'ssh_key', label: 'ssh_key (clave privada)' },
            { key: 'api_key', label: 'API Key (opcional)' },
        ],
    },
    vastai: {
        options: [
            { key: 'host', label: 'host SSH', placeholder: 'root@ssh4.vast.ai' },
            { key: 'port', label: 'puerto SSH', placeholder: '12345' },
            { key: 'remote_bin', label: 'remote_bin', placeholder: 'gestiora' },
            { key: 'workdir', label: 'workdir', placeholder: '/tmp/gestiora-worker' },
        ],
        secrets: [
            { key: 'ssh_key', label: 'ssh_key (clave privada)' },
            { key: 'api_key', label: 'API Key (opcional)' },
        ],
    },
    sibling: {
        options: [
            { key: 'url', label: 'URL de la API Sibling', placeholder: 'https://sibling-gestiora-instance.com' },
        ],
        secrets: [
            { key: 'token', label: 'token (Token de clúster compartido)' },
        ],
    },
    docker: {
        options: [
            { key: 'image', label: 'imagen', placeholder: 'gestiora-transcoding-core:dev' },
            { key: 'engine', label: 'engine (opcional)', placeholder: 'docker' },
        ],
        secrets: [],
    },
    podman: {
        options: [
            { key: 'image', label: 'imagen', placeholder: 'gestiora-transcoding-core:dev' },
            { key: 'engine', label: 'engine (opcional)', placeholder: 'podman' },
        ],
        secrets: [],
    },
    k8s: {
        options: [
            { key: 'image', label: 'imagen', placeholder: 'gestiora-transcoding-core:dev' },
            { key: 'namespace', label: 'namespace (opcional)', placeholder: 'default' },
            { key: 'context', label: 'contexto kubeconfig (opcional)', placeholder: 'mi-cluster' },
        ],
        secrets: [],
    },
    clusters: {
        options: [
            { key: 'repo', label: 'checkout de GESTIORA_CLUSTERS', placeholder: '/opt/GESTIORA_CLUSTERS' },
            { key: 'image', label: 'imagen (con gestiora-core)', placeholder: 'registry/gestiora-core:dev' },
            { key: 'cpu', label: 'CPU (máx 2)', placeholder: '1' },
            { key: 'mem', label: 'memoria (máx 4Gi)', placeholder: '2Gi' },
            { key: 'kubeconfig', label: 'kubeconfig (opcional)', placeholder: '…/kubeconfig-worker-acer.yaml' },
        ],
        secrets: [],
    },
};
const EMPTY = {
    name: '',
    transport: 'local',
    weight: 1,
    enabled: true,
    options: {},
    secrets: {},
    notes: '',
};
export default function WorkersManager() {
    const { t } = useTranslation();
    const { workers: userWorkers, refresh } = useWorkers();
    const { workers, userTotal, fleetTotal, loading, error, refresh: refreshPool, } = useWorkerPool();
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    // Resultado de «Probar conexión» por worker: { busy } | { ok, detail }.
    const [checks, setChecks] = useState({});
    // Rendimiento del pool: benchmark + emulador de potencia (capacity).
    const [benchWork, setBenchWork] = useState(64);
    const [benchBusy, setBenchBusy] = useState(false);
    const [profiles, setProfiles] = useState(null);
    const [capWork, setCapWork] = useState(1024);
    const [capBusy, setCapBusy] = useState(false);
    const [cap, setCap] = useState(null);
    const [perfMsg, setPerfMsg] = useState(null);
    const spec = TRANSPORT_FIELDS[form.transport];
    const onBenchmark = async () => {
        setBenchBusy(true);
        setPerfMsg(null);
        try {
            const r = await benchmarkPool(benchWork);
            setProfiles(r.profiles);
        }
        catch (err) {
            setPerfMsg({ kind: 'err', text: err instanceof Error ? err.message : String(err) });
        }
        finally {
            setBenchBusy(false);
        }
    };
    const onCapacity = async () => {
        setCapBusy(true);
        setPerfMsg(null);
        try {
            setCap(await getCapacity(capWork));
        }
        catch (err) {
            setPerfMsg({ kind: 'err', text: t('perf.needBenchmark') });
        }
        finally {
            setCapBusy(false);
        }
    };
    const onValidate = async (w) => {
        setChecks((c) => ({ ...c, [w.id]: { busy: true } }));
        try {
            const r = await validateWorker(w.id);
            setChecks((c) => ({ ...c, [w.id]: { ok: r.ok, detail: r.detail } }));
        }
        catch (err) {
            setChecks((c) => ({
                ...c,
                [w.id]: { ok: false, detail: err instanceof Error ? err.message : String(err) },
            }));
        }
    };
    const resetForm = () => {
        setForm(EMPTY);
        setEditingId(null);
    };
    const startEdit = (w) => {
        setEditingId(w.id);
        setForm({
            name: w.name,
            transport: w.transport,
            weight: w.weight,
            enabled: w.enabled,
            options: { ...w.options },
            secrets: {}, // nunca llegan al cliente; vacío = conservar los previos
            notes: w.notes ?? '',
        });
        setMsg(null);
    };
    const setOption = (key, value) => setForm((f) => ({ ...f, options: { ...f.options, [key]: value } }));
    const setSecret = (key, value) => setForm((f) => ({ ...f, secrets: { ...f.secrets, [key]: value } }));
    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMsg(null);
        try {
            if (editingId)
                await updateWorker(editingId, form);
            else
                await createWorker(form);
            setMsg({ kind: 'ok', text: t('workers.saved') });
            resetForm();
            refresh();
            refreshPool();
        }
        catch (err) {
            setMsg({ kind: 'err', text: err instanceof Error ? err.message : String(err) });
        }
        finally {
            setBusy(false);
        }
    };
    const onDelete = async (w) => {
        if (!window.confirm(t('workers.confirmDelete', { name: w.name })))
            return;
        try {
            await deleteWorker(w.id);
            if (editingId === w.id)
                resetForm();
            refresh();
            refreshPool();
        }
        catch (err) {
            setMsg({ kind: 'err', text: err instanceof Error ? err.message : String(err) });
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs("article", { className: "card form-card", children: [_jsx("h2", { children: t('workers.title') }), _jsx("p", { className: "muted", children: t('workers.intro') }), loading && _jsx("p", { className: "muted", children: t('common.loading') }), error && _jsx("p", { className: "error", children: error }), !loading && workers.length === 0 && _jsx("p", { className: "muted", children: t('workers.empty') }), workers.length > 0 && (_jsxs(_Fragment, { children: [_jsx("p", { className: "muted", children: t('workers.poolSummary', { total: workers.length, user: userTotal, fleet: fleetTotal }) }), _jsxs("table", { className: "data-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: t('workers.col.name') }), _jsx("th", { children: t('workers.col.source') }), _jsx("th", { children: t('workers.col.transport') }), _jsx("th", { children: t('workers.col.weight') }), _jsx("th", { children: t('workers.col.enabled') }), _jsx("th", { children: t('workers.col.available') }), _jsx("th", { children: t('workers.col.secrets') }), _jsx("th", { children: t('workers.notes') }), _jsx("th", { children: t('workers.col.connection') }), _jsx("th", {})] }) }), _jsx("tbody", { children: workers.map((w) => {
                                            const chk = checks[w.id];
                                            const isFleet = w.source === 'fleet';
                                            return (_jsxs("tr", { children: [_jsx("td", { children: w.name }), _jsx("td", { children: t(`workers.source.${w.source ?? 'user'}`) }), _jsx("td", { children: w.transport }), _jsx("td", { children: w.weight }), _jsx("td", { children: w.enabled ? '✓' : '—' }), _jsx("td", { children: w.available ? '✓' : _jsx("span", { className: "error", children: "\u2717" }) }), _jsx("td", { children: w.secretFields.length > 0 ? w.secretFields.join(', ') : '—' }), _jsx("td", { className: "trunc", style: { maxWidth: '12rem' }, title: w.notes ?? '', children: (() => {
                                                            const email = w.options.account_email;
                                                            const bits = [email, w.notes ?? undefined].filter(Boolean);
                                                            return bits.length > 0 ? bits.join(' · ') : '—';
                                                        })() }), _jsx("td", { children: chk?.busy ? (_jsx("span", { className: "muted", children: t('workers.checking') })) : chk ? (_jsxs("span", { className: chk.ok ? 'ok-text' : 'error', title: chk.detail, children: [chk.ok ? '✓' : '✗', " ", chk.detail] })) : ('—') }), _jsx("td", { children: isFleet ? (_jsx("span", { className: "muted", children: t('workers.fleetManaged') })) : (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "btn btn-ghost", disabled: chk?.busy, onClick: () => void onValidate(w), children: t('workers.validate') }), _jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => startEdit(w), children: t('workers.edit') }), _jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => void onDelete(w), children: t('workers.delete') })] })) })] }, w.id));
                                        }) })] })] })), _jsx("h3", { children: editingId ? t('workers.editTitle') : t('workers.addTitle') }), userWorkers.length === 0 && fleetTotal > 0 && (_jsx("p", { className: "muted", children: t('workers.fleetOnly') })), _jsxs("form", { onSubmit: onSubmit, children: [_jsxs("div", { className: "grid-2", children: [_jsxs("label", { children: [t('workers.col.name'), _jsx("input", { type: "text", required: true, value: form.name, onChange: (e) => setForm((f) => ({ ...f, name: e.target.value })), placeholder: "colab-a" })] }), _jsxs("label", { children: [t('workers.col.transport'), _jsx("select", { value: form.transport, onChange: (e) => setForm((f) => ({ ...f, transport: e.target.value, options: {}, secrets: {} })), children: Object.keys(TRANSPORT_FIELDS).map((tr) => (_jsx("option", { value: tr, children: tr }, tr))) })] }), _jsxs("label", { children: [t('workers.col.weight'), _jsx("input", { type: "number", min: 0.1, step: 0.1, value: form.weight, onChange: (e) => setForm((f) => ({ ...f, weight: Number(e.target.value) })) })] }), _jsxs("label", { className: "checkbox-label", children: [_jsx("input", { type: "checkbox", checked: form.enabled, onChange: (e) => setForm((f) => ({ ...f, enabled: e.target.checked })) }), t('workers.col.enabled')] })] }), spec.options.length > 0 && (_jsx("div", { className: "grid-2", children: spec.options.map((o) => (_jsxs("label", { children: [o.label, _jsx("input", { type: "text", value: form.options[o.key] ?? '', placeholder: o.placeholder, onChange: (e) => setOption(o.key, e.target.value) })] }, o.key))) })), spec.secrets.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "muted", children: ["\uD83D\uDD12 ", t('workers.secretsNote'), editingId ? ` ${t('workers.secretsKeepNote')}` : ''] }), _jsx("div", { className: "grid-2", children: spec.secrets.map((s) => (_jsxs("label", { children: [s.label, _jsx("input", { type: "password", autoComplete: "new-password", value: form.secrets[s.key] ?? '', onChange: (e) => setSecret(s.key, e.target.value) }), s.key === 'kaggle_token' && (_jsx("input", { type: "file", accept: ".json,application/json", onChange: (e) => {
                                                        const f = e.target.files?.[0];
                                                        if (!f)
                                                            return;
                                                        const reader = new FileReader();
                                                        reader.onload = () => setSecret('kaggle_token', String(reader.result));
                                                        reader.readAsText(f);
                                                    } }))] }, s.key))) })] })), _jsxs("label", { children: [t('workers.notes'), _jsx("textarea", { rows: 2, value: form.notes ?? '', placeholder: t('workers.notesPlaceholder'), onChange: (e) => setForm((f) => ({ ...f, notes: e.target.value })) })] }), msg && _jsx("p", { className: msg.kind === 'ok' ? 'ok-text' : 'error', children: msg.text }), _jsxs("div", { className: "btn-row", children: [_jsx("button", { type: "submit", className: "btn btn-primary", disabled: busy || form.name.trim() === '', children: busy ? t('common.loading') : editingId ? t('workers.save') : t('workers.add') }), editingId && (_jsx("button", { type: "button", className: "btn btn-ghost", onClick: resetForm, children: t('workers.cancel') }))] })] })] }), _jsxs("article", { className: "card form-card", children: [_jsx("h2", { children: t('perf.title') }), _jsx("p", { className: "muted", children: t('perf.intro') }), perfMsg && _jsx("p", { className: perfMsg.kind === 'ok' ? 'ok-text' : 'error', children: perfMsg.text }), _jsxs("div", { className: "btn-row", children: [_jsxs("label", { className: "inline-field", children: [t('perf.work'), _jsx("input", { type: "number", min: 1, max: 8192, value: benchWork, onChange: (e) => setBenchWork(Number(e.target.value)) })] }), _jsx("button", { type: "button", className: "btn btn-primary", disabled: benchBusy, onClick: () => void onBenchmark(), children: benchBusy ? t('perf.measuring') : t('perf.benchmark') })] }), profiles && (_jsxs("table", { className: "data-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: t('workers.col.name') }), _jsx("th", { children: t('workers.col.transport') }), _jsx("th", { children: "MiB/s" }), _jsx("th", { children: t('perf.latency') }), _jsx("th", { children: t('workers.col.available') })] }) }), _jsx("tbody", { children: profiles.map((p) => (_jsxs("tr", { children: [_jsx("td", { children: p.name }), _jsx("td", { children: p.transport }), _jsx("td", { children: p.ok ? p.throughput_mib_s.toFixed(0) : '—' }), _jsx("td", { children: p.ok ? `${p.latency_s.toFixed(1)} s` : '—' }), _jsx("td", { children: p.ok ? '✓' : _jsx("span", { className: "error", title: p.detail, children: "\u2717" }) })] }, p.name))) })] })), _jsx("h3", { children: t('perf.powerTitle') }), _jsxs("div", { className: "btn-row", children: [_jsxs("label", { className: "inline-field", children: [t('perf.jobSize'), _jsx("input", { type: "number", min: 1, value: capWork, onChange: (e) => setCapWork(Number(e.target.value)) })] }), _jsx("button", { type: "button", className: "btn btn-ghost", disabled: capBusy, onClick: () => void onCapacity(), children: capBusy ? t('common.loading') : t('perf.estimate') })] }), cap && (_jsxs("div", { className: "perf-report", children: [_jsxs("p", { children: [_jsxs("strong", { children: [t('perf.unifiedPower'), ":"] }), ' ', cap.capacity.total_throughput_mib_s.toFixed(0), " MiB/s", ' ', "(~", cap.capacity.equivalent_cores.toFixed(1), " ", t('perf.cores'), ", ", cap.capacity.workers_ok, " workers)"] }), _jsxs("p", { children: [t('perf.forJob', { mib: cap.advice.work_mib }), ":", ' ', _jsxs("strong", { children: [cap.advice.best_makespan_s.toFixed(1), " s"] }), ' ', "(", cap.advice.best_subset.join(', ') || '—', ") vs ", cap.advice.single_best_name, ' ', cap.advice.single_best_s.toFixed(1), " s \u2192 \u00D7", cap.advice.speedup_vs_single.toFixed(2)] }), _jsx("p", { className: cap.advice.distribuir_merece_la_pena ? 'ok-text' : 'error', children: cap.advice.nota })] }))] })] }));
}
